import { Plus, X } from 'lucide-react';
import { useFieldArray, useWatch, type Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TimePicker } from '@nucleus-commerce/admin-kit/date-time';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';

import { useStore } from '@/shared/store/store-context';

import { fromHours, toHoursPatch } from '../api/mappers';
import { useChatSettingsForm, type ChatSettingsTabProps } from '../hooks/use-chat-settings-form';
import {
  hoursSchema,
  MAX_SEGMENTS_PER_DAY,
  WEEK_DAYS,
  type HoursForm,
  type WeekDay,
} from '../schemas/hours-schema';

import { FormAlert, SaveButton, SwitchField } from './form-controls';
import { TimezoneCombobox } from './timezone-combobox';

const GROUPS = ['hours', 'behaviour.autoAssign'] as const;

function DayRow({
  control,
  day,
  disabled,
  timeZone,
}: {
  control: Control<HoursForm>;
  day: WeekDay;
  disabled: boolean;
  /** The zone the hours are kept in, so the pickers' "Now" is that clock. */
  timeZone: string;
}) {
  const { t } = useTranslation('chatSettings');
  const { fields, append, remove } = useFieldArray({ control, name: `hours.days.${day}` });
  const dayName = t(`hours.days.${day}`);
  const segments = useWatch({ control, name: `hours.days.${day}` });
  const last = segments.at(-1);

  return (
    <div
      role="group"
      aria-label={dayName}
      className="flex flex-col gap-2 border-b py-3 last:border-b-0 sm:flex-row sm:items-start"
    >
      <span className="w-28 shrink-0 pt-2 text-sm font-medium">{dayName}</span>
      <div className="flex flex-1 flex-col gap-2">
        {fields.length === 0 ? (
          <span className="text-muted-foreground pt-2 text-sm">{t('hours.closed')}</span>
        ) : null}
        {fields.map((segment, index) => (
          <div key={segment.id} className="flex flex-wrap items-start gap-2">
            <FormField
              control={control}
              name={`hours.days.${day}.${index}.from`}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <TimePicker
                      className="w-32"
                      aria-label={t('hours.from', { day: dayName, n: index + 1 })}
                      disabled={disabled}
                      timeZone={timeZone}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <span className="text-muted-foreground pt-2 text-sm">{t('hours.to')}</span>
            <FormField
              control={control}
              name={`hours.days.${day}.${index}.to`}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <TimePicker
                      className="w-32"
                      aria-label={t('hours.until', { day: dayName, n: index + 1 })}
                      disabled={disabled}
                      timeZone={timeZone}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {disabled ? null : (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('hours.removeSegment', { day: dayName, n: index + 1 })}
                onClick={() => remove(index)}
              >
                <X aria-hidden />
              </Button>
            )}
          </div>
        ))}
      </div>
      {disabled || fields.length >= MAX_SEGMENTS_PER_DAY ? null : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          aria-label={t('hours.addSegmentFor', { day: dayName })}
          onClick={() =>
            append(last ? { from: last.to, to: '23:59' } : { from: '09:00', to: '18:00' })
          }
        >
          <Plus aria-hidden />
          {t('hours.addSegment')}
        </Button>
      )}
    </div>
  );
}

/**
 * Business hours and what happens inside them when nobody is online. `behaviour.autoAssign`
 * sits here, next to "offline when no agent is online", because together they decide whether a
 * shopper reaches a person, waits in a queue or gets the offline form (LC1).
 */
export function HoursTab({ storeId, settings, readOnly }: ChatSettingsTabProps) {
  const { t } = useTranslation('chatSettings');
  const storeTimezone = useStore().timezone;
  const { form, submit, saving } = useChatSettingsForm<HoursForm>({
    storeId,
    schema: hoursSchema,
    defaultValues: fromHours(settings),
    toPatch: toHoursPatch,
    groups: GROUPS,
    readOnly,
  });
  const enabled = useWatch({ control: form.control, name: 'hours.enabled' });
  const offlineWhenNone = useWatch({
    control: form.control,
    name: 'hours.offlineWhenNoAgentOnline',
  });
  const autoAssign = useWatch({ control: form.control, name: 'behaviour.autoAssign' });
  // A blank timezone means the store's.
  const hoursTimezone =
    useWatch({ control: form.control, name: 'hours.timezone' }) || storeTimezone;

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex max-w-3xl flex-col gap-6">
        <FormAlert message={form.formState.errors.root?.server?.message} />
        <FormSection title={t('hours.title')} description={t('hours.description')}>
          <SwitchField
            control={form.control}
            name="hours.enabled"
            label={t('fields.hoursEnabled')}
            description={t('fields.hoursEnabledHint')}
            disabled={readOnly}
          />
          <FormField
            control={form.control}
            name="hours.timezone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('fields.timezone')}</FormLabel>
                <FormControl>
                  <TimezoneCombobox
                    value={field.value}
                    onChange={field.onChange}
                    storeTimezone={storeTimezone}
                    disabled={readOnly}
                  />
                </FormControl>
                <FormDescription>{t('fields.timezoneHint')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className={enabled ? undefined : 'opacity-60'}>
            {WEEK_DAYS.map((day) => (
              <DayRow
                key={day}
                control={form.control}
                day={day}
                disabled={readOnly}
                timeZone={hoursTimezone}
              />
            ))}
          </div>
        </FormSection>
        <FormSection title={t('availability.title')} description={t('availability.description')}>
          <SwitchField
            control={form.control}
            name="hours.offlineWhenNoAgentOnline"
            label={t('fields.offlineWhenNoAgentOnline')}
            description={t('fields.offlineWhenNoAgentOnlineHint')}
            disabled={readOnly}
          />
          <FormField
            control={form.control}
            name="behaviour.autoAssign"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('fields.autoAssign')}</FormLabel>
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={readOnly}
                  className="gap-3"
                >
                  {(['off', 'round_robin'] as const).map((value) => (
                    <div key={value} className="flex items-start gap-2">
                      <RadioGroupItem
                        value={value}
                        id={`auto-assign-${value}`}
                        className="mt-0.5"
                      />
                      <div className="grid gap-0.5">
                        <Label htmlFor={`auto-assign-${value}`}>
                          {t(`fields.autoAssignOption.${value}`)}
                        </Label>
                        <span className="text-muted-foreground text-sm">
                          {t(`fields.autoAssignHint.${value}`)}
                        </span>
                      </div>
                    </div>
                  ))}
                </RadioGroup>
                <FormMessage />
              </FormItem>
            )}
          />
          <Alert>
            <AlertDescription data-testid="availability-summary">
              {t(`availability.summary.${offlineWhenNone ? 'offline' : 'away'}_${autoAssign}`)}
            </AlertDescription>
          </Alert>
        </FormSection>
        <SaveButton saving={saving} dirty={form.formState.isDirty} hidden={readOnly} />
      </form>
    </Form>
  );
}
